"use client";

// WO-GF-001-P1B-S12 · RW2-3 — SCHEDULE DATA LAYER. Document-scoped hydrator (same family as
// SiteLinkLayer / CarouselLayer / DatePickerLayer): it makes the transport-company profile's dead schedule
// controls real, driving them off the LIVE-CAPTURED payload (lib/schedule-data.ts ← schedule-capture.mjs
// ← live's own `__NEXT_DATA__.props.pageProps.schedules`). Nothing here is synthesised: every stop, time,
// direction and season is the operator's real published data.
//
// FOUNDER FINDING (re-walk 2026-07-22, RW2-3): "Transport-company profiles: schedule dropdowns dead —
// selecting a schedule must switch the displayed schedule AND the to/from ticket prices respectively.
// Real data only (BE/live-derived); never fake a schedule."
//
// TWO controls, both dead in the mirror, both wired here:
//   1. "Select Schedule Date Range" <select> — the operator's real seasons (Epic Rides publishes 8).
//   2. the TO/FROM direction toggle — the season's two real routes (…-to-whistler / whistler-to-…).
// Changing either re-renders the schedule table from the payload.
//
// PRICES — an honest limit, recorded rather than invented (F81). The founder expects the to/from toggle to
// move the ticket prices. On LIVE it does not: verified 2026-07-22 on BOTH operators — toggling TO→FROM
// leaves the price table byte-identical (Epic Rides "W Standard $34 $45 …" unchanged; Skylynx "Destination
// Vancouver" + its EV/XV/V zones unchanged). Live's price table is per-operator/destination, not
// per-direction. So this layer renders the real `pricing` payload and moves it exactly when live moves it —
// i.e. not on direction. Faking a direction-dependent fare would invent money data that no source
// publishes, which is the one thing the transport rails forbid. Flagged in §S12 for the founder's ruling.
//
// PIXEL SAFETY: the layer writes NOTHING until the user changes a control — at rest the captured table is
// untouched, so the at-rest pixel baselines are unaffected. Idempotent + fully cleaned up on unmount.

import { useEffect } from "react";
import type {
  ScheduleRoute,
  SchedulePayload,
  ScheduleStop,
} from "@/lib/schedule-data";

// Column header label → the timetable day keys it displays. Live merges Sat & Sun into one column; we read
// the labels off the CAPTURED header rather than assuming a column set, so the table keeps live's shape.
const DAY_KEYS: [RegExp, string[]][] = [
  [/sat\s*&\s*sun/i, ["SATURDAY", "SUNDAY"]],
  [/^sun/i, ["SUNDAY"]],
  [/^sat/i, ["SATURDAY"]],
  [/^mon/i, ["MONDAY"]],
  [/^tue/i, ["TUESDAY"]],
  [/^wed/i, ["WEDNESDAY"]],
  [/^thu/i, ["THURSDAY"]],
  [/^fri/i, ["FRIDAY"]],
];

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// "05:40 AM" → `05:40 ` · "01:00 PM" → `<b>01:00 </b>` (live drops the meridiem and bolds PM; the table's
// own legend says "PM - TIME SHOWN IN BOLD"). Markup matches live's cells exactly.
function timeCell(times: string[]): string {
  return times
    .map((t) => {
      const m = /^(\d{1,2}:\d{2})\s*(AM|PM)?/i.exec(t.trim());
      const hhmm = m ? m[1] : t.trim();
      const pm = m && /pm/i.test(m[2] || "");
      const inner = `${esc(hhmm ?? "")} `;
      return `<h5 style="font-size: inherit; font-weight: inherit;">${pm ? `<b>${inner}</b>` : inner}</h5>`;
    })
    .join("");
}

function stopHeaderCell(stop: ScheduleStop, index: number, total: number): string {
  // The numeric circle appears only on multi-stop routes (live: a single-stop summer route renders just the
  // zone circle; the 4-stop winter route renders zone + 1..4). Observed, not assumed.
  const zone = stop.zoneName
    ? `<div class="stop-cir" style="border: 1.5px solid rgb(241, 90, 34);">${esc(stop.zoneName)}</div>`
    : "";
  const num = total > 1 ? `<div class="stop-cir">${index + 1}</div>` : "";
  return `<th scope="row" class="pl-21 width-250"><div class="d-flex align-items-center p-0 mx-0">${zone}${num}<h4><p class="stop-n">${esc(stop.name)}</p></h4></div></th>`;
}

export default function ScheduleDataLayer({
  payload,
}: {
  payload: SchedulePayload;
}) {
  useEffect(() => {
    const table = document.querySelector<HTMLTableElement>(
      ".schedule-table table",
    );
    const select = document.querySelector<HTMLSelectElement>(
      "select.sc-table-form-dropdown",
    );
    if (!table || !select) return; // page shape differs → do nothing
    const tbody = table.tBodies[0];
    const headRow = table.tHead?.rows[0];
    if (!tbody || !headRow) return;

    // The captured header's day columns, in the captured order — the render target's shape comes from the
    // mirror, never from an assumption about which days an operator runs.
    const dayCols = Array.from(headRow.cells)
      .slice(1)
      .map((c) => {
        const label = (c.textContent || "").trim();
        const hit = DAY_KEYS.find(([re]) => re.test(label));
        return hit ? hit[1] : [];
      });

    const directionBtns = Array.from(
      document.querySelectorAll<HTMLElement>(".btn-none"),
      // NB: no \b after TO/FROM — live nests the label as <span>TO</span><div>Whistler</div>, so the
      // button's textContent is "TOWhistler" with no separator.
    ).filter((b) => /^\s*(TO|FROM)/i.test(b.textContent || ""));

    // Direction index: live orders the toggle TO (route 0, …-to-<mountain>) then FROM (route 1).
    let dirIdx = Math.max(
      0,
      directionBtns.findIndex((b) =>
        b.closest(".input-group")?.className.includes("input-group-active"),
      ),
    );

    // ── FOUNDER-WALK-QA-STAGING-007 — THE FALLBACK-TO-FIRST-ELEMENT, REMOVED ──────────────────────
    //
    // This was `?? payload.schedules[0]`: the exact shape CLAUDE.md §11 registers as "a fabrication
    // generator", and the one already found and fixed once in this codebase (`|| WHIS_ROUTES[0]`,
    // WO-COMMERCE-BIND-001 · DoD 6, where an unmatched route slug rendered the FIRST route's heading
    // and body under another route's URL).
    //
    // WHY IT WAS LIVE HERE, AND WHY IT GOT MORE DANGEROUS RATHER THAN LESS. The season <select>'s
    // <option> values come from a FROZEN CAPTURED mirror body, while `payload.schedules` is fetched
    // LIVE (`lib/schedule-data.ts`, moved off a frozen file by WO-COMMERCE-BIND-001-DATA-RECONNECT
    // precisely because captures go stale). The two are no longer captured together, so the moment
    // live renames, adds or drops a season, picking a real-looking option rendered WHICHEVER season
    // happens to be `schedules[0]` — under the label the customer chose. Wrong times, wrong dates,
    // right-looking heading.
    //
    // An unmatched season now resolves to `undefined`, and the caller's existing `season?.routes` /
    // `if (!route) return;` already declines to paint. Rendering nothing is the honest answer to
    // "we do not have that season"; borrowing another season's timetable is not.
    const seasonFor = (value: string) => payload.schedules.find((s) => s.name === value);

    function render() {
      const season = seasonFor(select!.value);
      const route: ScheduleRoute | undefined = season?.routes?.[dirIdx];
      if (!route) return;
      const stops = route.details ?? [];

      // header: first cell is the direction label. Live names the MOUNTAIN on both sides ("To Whistler" /
      // "From Whistler"), NOT the far endpoint — so the noun comes from the to-mountain route's
      // destination, not from whichever route is currently shown. (Verified on live 2026-07-22: toggling
      // FROM yields "From Whistler", never "From Vancouver & YVR Airport".)
      const mountainName =
        season?.routes?.[0]?.destination || route.destination || "";
      const lead = headRow!.cells[0];
      if (lead)
        lead.textContent = `${dirIdx === 0 ? "To" : "From"} ${mountainName}`.trim();

      tbody!.innerHTML = stops
        .map((stop, i) => {
          const tt = stop.timetable || {};
          const tds = dayCols
            .map((keys) => {
              // A merged column ("Sat & Sun") covers two day keys; live renders the shared departure list
              // ONCE, so identical times are de-duplicated. If an operator ever ran different Sat/Sun
              // times the union still shows both rather than silently dropping one.
              const times = [...new Set(keys.flatMap((k) => tt[k] ?? []))];
              return `<td>${timeCell(times)}</td>`;
            })
            .join("");
          return `<tr>${stopHeaderCell(stop, i, stops.length)}${tds}</tr>`;
        })
        .join("");

      table!.setAttribute("data-schedule-season", season?.name ?? "");
      table!.setAttribute("data-schedule-route", route.name ?? "");
    }

    const onSelect = () => render();
    select.addEventListener("change", onSelect);

    const onDir = (e: Event) => {
      e.preventDefault();
      const btn = e.currentTarget as HTMLElement;
      const idx = directionBtns.indexOf(btn);
      if (idx < 0) return;
      dirIdx = idx;
      // live moves the `input-group-active` marker onto the chosen side
      directionBtns.forEach((b, i) => {
        const g = b.closest(".input-group");
        if (!g) return;
        g.classList.toggle("input-group-active", i === idx);
      });
      render();
    };
    directionBtns.forEach((b) => {
      b.setAttribute("data-behavior", "schedule-direction");
      b.style.cursor = "pointer";
      b.addEventListener("click", onDir);
    });
    select.setAttribute("data-behavior", "schedule-season");

    return () => {
      select.removeEventListener("change", onSelect);
      directionBtns.forEach((b) => b.removeEventListener("click", onDir));
    };
  }, [payload]);

  return null;
}
